import { useCallback, useEffect, useMemo, useState } from 'react';

import { head } from 'lodash';

export interface KeyValueItem {
	key: string;
	value: string | number;
}

export interface KeyValueDefinition extends KeyValueItem {
	id: string;
}

interface Props {
	keyValueDefinition: KeyValueItem[];
	onChange: (pairs: KeyValueItem[]) => void;
	allowEmpty?: boolean;
	translations: {
		errors: {
			emptyRowMessage: string;
			keyAndValueNotEmpty: string;
			noSameValue: string;
		};
	};
}

const createUniqDefinitionItemId = (index: number) => {
	return `${index + 1}-${Date.now()}`;
};

const getEmptyDefinitionItem = (index = 0): KeyValueDefinition => {
	return {
		value: '',
		key: '',
		id: createUniqDefinitionItemId(index)
	};
};

const normalizeValue = (value: string | number) => {
	const valueAsNumber = Number(value);

	return Number.isFinite(valueAsNumber) ? valueAsNumber : value;
};

export function useKeyValueDefinitionTableState({
	keyValueDefinition,
	onChange,
	allowEmpty = false,
	translations: { errors }
}: Props) {
	const [definitionList, setDefinitionList] = useState<KeyValueDefinition[]>([]);

	useEffect(() => {
		const definitionList: KeyValueDefinition[] = keyValueDefinition?.length
			? keyValueDefinition.map(({ key, value: originalValue }, index) => ({
					key: String(key),
					// on UI value casted to string for straightforward validation
					value: String(originalValue),
					id: createUniqDefinitionItemId(index + 1)
			  }))
			: [getEmptyDefinitionItem()];

		setDefinitionList(definitionList);
	}, [keyValueDefinition]);

	const enumDefinitionError = useMemo(() => {
		let error = '';
		const enumKeys = new Set();

		if (!allowEmpty && !definitionList.length) {
			error = errors.emptyRowMessage;
			return error;
		}

		for (const definition of definitionList) {
			const { key, value } = definition;

			const trimmedValue = String(value).trim();
			const trimmedKey = String(key).trim();
			if (!trimmedKey.length || !trimmedValue.length) {
				error = errors.keyAndValueNotEmpty;
				return error;
			}

			enumKeys.add(trimmedKey);
		}

		if (enumKeys.size !== definitionList.length) {
			error = errors.noSameValue;
			return error;
		}

		return error; /* eslint-disable-line */
	}, [definitionList, allowEmpty, errors]);

	useEffect(() => {
		if (!enumDefinitionError) {
			onChange(
				definitionList.map(({ key, value }) => ({
					key: String(key),
					value: normalizeValue(value)
				}))
			);
		}
	}, [enumDefinitionError, definitionList]);

	const handleDefinitionChange = useCallback(
		(index: number, newDefinition: KeyValueDefinition) => {
			const newEnumDefinition = [...definitionList];
			newEnumDefinition[index] = newDefinition;

			setDefinitionList(newEnumDefinition);
		},
		[definitionList]
	);

	const handleDelete = useCallback(
		(deleteIndex: number) => {
			setDefinitionList(definitionList.filter((_, index) => index !== deleteIndex));
		},
		[definitionList]
	);

	const addNewValue = useCallback(() => {
		setDefinitionList([...definitionList, getEmptyDefinitionItem(definitionList.length)]);
	}, [definitionList]);

	const [orderedDefinitionList, setOrderedDefinitionList] = useState<KeyValueDefinition[]>([]);

	useEffect(() => {
		setOrderedDefinitionList(definitionList);
	}, [definitionList]);

	const handleMoveItem = useCallback(
		(dragIndex: number, hoverIndex: number) => {
			const newOrder = [...orderedDefinitionList];

			const dragItem = head(newOrder.splice(dragIndex, 1));
			newOrder.splice(hoverIndex, 0, dragItem!);

			setOrderedDefinitionList(newOrder);
		},
		[orderedDefinitionList]
	);

	const handleDroppedItem = useCallback(() => {
		setDefinitionList(orderedDefinitionList);
	}, [orderedDefinitionList]);

	return {
		definitionList,
		setDefinitionList,
		enumDefinitionError,
		handleDefinitionChange,
		handleDelete,
		addNewValue,
		orderedDefinitionList,
		handleMoveItem,
		handleDroppedItem
	};
}
